/**
 * Cell Operations
 *
 * TypeScript implementation of cell state operations.
 * Replaces legacy functions: setSzines, setHid, setUres, setAbra, setFekete,
 * setVvonal, setFvonal, setIsmert.
 */

import type { Cell } from '../core/Cell';
import type { Puzzle } from '../core/Puzzle';
import type { HistoryManager } from '../core/HistoryManager';
import type { AppState } from '../state/AppState';
import { domToCellId } from '../events/CellEvents';

/** Cell type constants for class manipulation */
const CELL_TYPES = ['színes', 'híd', 'fekete', 'ábra', 'üres'] as const;
type CellType = (typeof CELL_TYPES)[number];

/** Context for cell operations */
export interface CellOperationContext {
    puzzle: Puzzle;
    history: HistoryManager;
    appState: AppState;
    onMenuUpdate: () => void;
    onHistoryUpdate: () => void;
}

/**
 * Create a regex to match cell types except the specified one
 */
function classReplace(except: CellType): RegExp {
    const types = CELL_TYPES.filter((t) => t !== except);
    return new RegExp(`(${types.join('|')})`, 'g');
}

/**
 * Toggle a class on a DOM element
 */
function toggleClass(element: HTMLElement, className: string): void {
    const currentClass = element.getAttribute('class') ?? '';
    if (currentClass.includes(className)) {
        element.className = currentClass.replace(className, '').trim();
    } else {
        element.className = `${currentClass} ${className}`.trim();
    }
}

/**
 * Remove other cell type classes and toggle the target class
 */
function updateCellClass(
    element: HTMLElement,
    cellType: CellType,
    clearContent = false,
): void {
    // Toggle the target class
    toggleClass(element, cellType);

    // Remove conflicting classes
    element.className = element.className.replace(classReplace(cellType), '');

    // Clear content if needed (for fekete, ábra, üres)
    if (clearContent) {
        element.innerHTML = '';
    }
}

/**
 * Get the synced cell element if sync editing is enabled
 */
function getSyncedCell(
    ctx: CellOperationContext,
    cellId: number,
): HTMLElement | null {
    if (!ctx.appState.syncEditing) return null;

    const nextCell = ctx.puzzle.getNextCell(cellId);
    if (!nextCell) return null;

    return document.getElementById(`mezo-${nextCell.id}`);
}

/**
 * Resolve a DOM element to its cell ID and Cell object.
 * Returns null if the element is not a valid cell.
 */
function getCellFromElement(
    element: HTMLElement,
    ctx: CellOperationContext,
): { cellId: number; cell: Cell } | null {
    const cellId = domToCellId(element);
    if (cellId === null) return null;
    const cell = ctx.puzzle.getCell(cellId);
    if (!cell) return null;
    return { cellId, cell };
}

/**
 * Apply an operation to the synced cell if sync editing is enabled
 * and the synced cell matches the given condition.
 */
function withSyncedCell(
    ctx: CellOperationContext,
    cellId: number,
    shouldSync: (syncedCell: Cell) => boolean,
    apply: (syncedEl: HTMLElement, syncedCell: Cell) => void,
): void {
    const syncedEl = getSyncedCell(ctx, cellId);
    if (!syncedEl) return;
    const syncedCellId = domToCellId(syncedEl);
    if (syncedCellId === null) return;
    const syncedCell = ctx.puzzle.getCell(syncedCellId);
    if (!syncedCell || !shouldSync(syncedCell)) return;
    apply(syncedEl, syncedCell);
}

/**
 * Save a change to history and update the UI.
 */
function commitChange(
    ctx: CellOperationContext,
    action: string,
    element: HTMLElement,
): void {
    ctx.history.save(action);
    ctx.onHistoryUpdate();
    ctx.onMenuUpdate();
    element.focus();
}

/**
 * Set colored cell (Megfejtés betűje)
 */
export function setColored(
    element: HTMLElement,
    ctx: CellOperationContext,
): void {
    const result = getCellFromElement(element, ctx);
    if (!result || !ctx.appState.allowColored) return;
    const { cellId, cell } = result;

    const wasColored = cell.isColored;

    withSyncedCell(
        ctx,
        cellId,
        (sc) => sc.isColored === wasColored,
        (el, sc) => {
            updateCellClass(el, 'színes');
            sc.toggleColored();
        },
    );

    updateCellClass(element, 'színes');
    cell.toggleColored();

    commitChange(
        ctx,
        wasColored ? 'Megfejtés betűje visszavonás' : 'Megfejtés betűje',
        element,
    );
}

/**
 * Set bridge cell (Híd betűje)
 */
export function setBridge(
    element: HTMLElement,
    ctx: CellOperationContext,
): void {
    const result = getCellFromElement(element, ctx);
    if (!result || !ctx.appState.allowBridge) return;
    const { cellId, cell } = result;

    const wasBridge = cell.isBridge;

    withSyncedCell(
        ctx,
        cellId,
        (sc) => sc.isBridge === wasBridge,
        (el, sc) => {
            updateCellClass(el, 'híd');
            sc.toggleBridge();
        },
    );

    updateCellClass(element, 'híd');
    cell.toggleBridge();

    commitChange(
        ctx,
        wasBridge ? 'Híd betűje visszavonás' : 'Híd betűje',
        element,
    );
}

/**
 * Set empty cell (Üresen marad)
 */
export function setEmpty(
    element: HTMLElement,
    ctx: CellOperationContext,
): void {
    const result = getCellFromElement(element, ctx);
    if (!result || !ctx.appState.allowEmpty || result.cell.isKnown) return;
    const { cellId, cell } = result;

    const wasEmpty = cell.isEmpty;

    withSyncedCell(
        ctx,
        cellId,
        (sc) => sc.isEmpty === wasEmpty,
        (el, sc) => {
            updateCellClass(el, 'üres', true);
            sc.toggleEmpty();
        },
    );

    updateCellClass(element, 'üres', true);
    cell.toggleEmpty();

    commitChange(ctx, wasEmpty ? 'Üres visszavonás' : 'Üres', element);
}

/**
 * Set image cell (Ábra helye)
 */
export function setImage(
    element: HTMLElement,
    ctx: CellOperationContext,
): void {
    const result = getCellFromElement(element, ctx);
    if (!result) return;
    const { cellId, cell } = result;

    if (
        !ctx.appState.allowImages ||
        cell.isKnown ||
        cell.hasVerticalLine ||
        cell.hasHorizontalLine
    )
        return;

    const wasImage = cell.isImage;

    withSyncedCell(
        ctx,
        cellId,
        (sc) => sc.isImage === wasImage,
        (el, sc) => {
            updateCellClass(el, 'ábra', true);
            sc.toggleImage();
        },
    );

    updateCellClass(element, 'ábra', true);
    cell.toggleImage();

    commitChange(ctx, wasImage ? 'Ábra visszavonás' : 'Ábra', element);
}

/**
 * Set black cell (Fekete négyzet)
 */
export function setBlack(
    element: HTMLElement,
    ctx: CellOperationContext,
): void {
    const result = getCellFromElement(element, ctx);
    if (!result) return;
    const { cellId, cell } = result;

    if (
        !ctx.appState.allowBlack ||
        cell.isKnown ||
        cell.hasVerticalLine ||
        cell.hasHorizontalLine
    )
        return;

    const wasBlack = cell.isBlack;

    withSyncedCell(
        ctx,
        cellId,
        (sc) => sc.isBlack === wasBlack,
        (el, sc) => {
            updateCellClass(el, 'fekete', true);
            sc.toggleBlack();
        },
    );

    updateCellClass(element, 'fekete', true);
    cell.toggleBlack();

    commitChange(
        ctx,
        wasBlack ? 'Fekete négyzet visszavonás' : 'Fekete négyzet',
        element,
    );
}

/**
 * Set vertical line (Vízszintes vonal)
 */
export function setVerticalLine(
    element: HTMLElement,
    ctx: CellOperationContext,
): void {
    const result = getCellFromElement(element, ctx);
    if (!result || !result.cell.verticalLineEnabled || result.cell.isBlack)
        return;
    const { cellId, cell } = result;

    const hadLine = cell.hasVerticalLine;

    withSyncedCell(
        ctx,
        cellId,
        (sc) => sc.hasVerticalLine === hadLine,
        (el, sc) => {
            sc.toggleVerticalLine();
            updateLineClass(el, 'vvonal', sc.hasVerticalLine);
        },
    );

    cell.toggleVerticalLine();
    updateLineClass(element, 'vvonal', cell.hasVerticalLine);

    commitChange(
        ctx,
        hadLine ? 'Vízszintes vonal visszavonás' : 'Vízszintes vonal',
        element,
    );
}

/**
 * Set horizontal line (Függőleges vonal)
 */
export function setHorizontalLine(
    element: HTMLElement,
    ctx: CellOperationContext,
): void {
    const result = getCellFromElement(element, ctx);
    if (!result || !result.cell.horizontalLineEnabled || result.cell.isBlack)
        return;
    const { cellId, cell } = result;

    const hadLine = cell.hasHorizontalLine;

    withSyncedCell(
        ctx,
        cellId,
        (sc) => sc.hasHorizontalLine === hadLine,
        (el, sc) => {
            sc.toggleHorizontalLine();
            updateLineClass(el, 'fvonal', sc.hasHorizontalLine);
        },
    );

    cell.toggleHorizontalLine();
    updateLineClass(element, 'fvonal', cell.hasHorizontalLine);

    commitChange(
        ctx,
        hadLine ? 'Függőleges vonal visszavonás' : 'Függőleges vonal',
        element,
    );
}

/**
 * Update line class on element
 */
function updateLineClass(
    element: HTMLElement,
    className: string,
    hasLine: boolean,
): void {
    const currentClass = element.getAttribute('class') ?? '';
    if (hasLine && !currentClass.includes(className)) {
        element.className = `${currentClass} ${className}`.trim();
    } else if (!hasLine && currentClass.includes(className)) {
        element.className = currentClass.replace(className, '').trim();
    }
}

/**
 * Set known cell (Ismert mező)
 */
export function setKnown(
    element: HTMLElement,
    ctx: CellOperationContext,
): void {
    const result = getCellFromElement(element, ctx);
    if (!result || !ctx.appState.isBuilderMode) return;
    const { cellId, cell } = result;

    const regex = new RegExp(ctx.appState.validCharPattern);
    if (!regex.test(cell.value || '')) return;

    const wasKnown = cell.isKnown;

    withSyncedCell(
        ctx,
        cellId,
        (sc) => sc.isKnown === wasKnown,
        (el, sc) => {
            sc.toggleKnown();
            updateKnownClass(el, sc.isKnown);
        },
    );

    cell.toggleKnown();
    updateKnownClass(element, cell.isKnown);

    commitChange(
        ctx,
        wasKnown ? 'Ismert mező visszavonás' : 'Ismert mező',
        element,
    );
}

/**
 * Update known class on element
 */
function updateKnownClass(element: HTMLElement, isKnown: boolean): void {
    const currentClass = element.getAttribute('class') ?? '';
    if (isKnown && !currentClass.includes('ismert')) {
        element.className = `${currentClass} ismert`.trim();
    } else if (!isKnown && currentClass.includes('ismert')) {
        element.className = currentClass.replace('ismert', '').trim();
    }
}
